/**
 * Root-level error boundary — catches errors that escape route-level
 * error.tsx (e.g. layout crashes). Cannot use usePathname() because the
 * entire app shell is gone; always classifies as blocking since a root
 * crash means nothing renders. Recovery is a hard reload so leftover
 * tabs pick up the latest client instead of re-rendering the stale bundle.
 */
"use client";

import { useEffect } from "react";
import { captureClientError } from "@/lib/sentry/capture-client";
import {
  ERROR_BOUNDARY_ACTION_LABEL,
  ERROR_BOUNDARY_COPY,
  ERROR_BOUNDARY_HEADING,
  hardReloadPage,
} from "@/lib/errorBoundaryRecovery";

export default function GlobalError({
  error,
}: {
  error: Error & { digest?: string };
  // reset is intentionally unused — we hard-reload to pick up the latest bundle
  reset: () => void;
}) {
  useEffect(() => {
    captureClientError(error, {
      feature: "error-boundary",
      errorType: "root-crash",
      impact: "blocking",
    });
  }, [error]);

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          fontFamily:
            "Inter, ui-sans-serif, system-ui, -apple-system, sans-serif",
          background: "#f8f8f8",
          color: "#1a1a1a",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
        }}
      >
        <div style={{ textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
          <h2 style={{ margin: 0, fontSize: "1.25rem", fontWeight: 600 }}>
            {ERROR_BOUNDARY_HEADING}
          </h2>
          <p style={{ margin: 0, fontSize: "0.875rem", color: "#6b7280" }}>
            {ERROR_BOUNDARY_COPY}
          </p>
          <button
            type="button"
            onClick={() => hardReloadPage("root")}
            style={{
              marginTop: 4,
              padding: "10px 20px",
              background: "#FF5500",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              fontSize: "0.875rem",
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            {ERROR_BOUNDARY_ACTION_LABEL}
          </button>
        </div>
      </body>
    </html>
  );
}
